/**
 * Column-major 4x4 orthographic view matrix builder for the metro map.
 *
 * Port of Android `feature/metro-map/.../render/ViewMatrixBuilder.kt`. Maps
 * canvas coordinates (0..CANVAS_SIZE, Y-down) into clip space (-1..1, Y-up)
 * with pan (offsetX/offsetY, in canvas units) and uniform zoom.
 *
 * The formula expresses a window of `viewportWidth / zoom × viewportHeight /
 * zoom` canvas units starting at `(offsetX, offsetY)`. Y is flipped via a
 * negative scale on the second basis vector so that SVG-style Y-down feeds
 * are rendered in the expected orientation.
 */

/**
 * Logical map canvas size in SVG units. The schema authors both metro-ios
 * (`MetroMap.swift`) and metro-android (`ConnectionProcessor.kt`) to a fixed
 * 0..4096 coordinate space; the web renderer follows the same convention so
 * that all three clients share the same VBO math.
 */
export const CANVAS_SIZE = 4096

/**
 * Build a column-major 4x4 view matrix as a flat `Float32Array` suitable for
 * `gl.uniformMatrix4fv(..., false, matrix)`.
 *
 * @param zoom            Uniform scale. `zoom = 1` means 1 canvas unit maps
 *                        to 1 viewport pixel. `zoom > 1` zooms in.
 * @param offsetX         X of the viewport's top-left in canvas units.
 * @param offsetY         Y of the viewport's top-left in canvas units.
 * @param viewportWidth   Viewport width in pixels (usually `canvas.width`).
 * @param viewportHeight  Viewport height in pixels (usually `canvas.height`).
 */
export function buildViewMatrix(
  zoom: number,
  offsetX: number,
  offsetY: number,
  viewportWidth: number,
  viewportHeight: number,
): Float32Array {
  const visibleWidth = viewportWidth / zoom
  const visibleHeight = viewportHeight / zoom

  const sx = 2 / visibleWidth
  const sy = -2 / visibleHeight // Y-flip: canvas Y-down → NDC Y-up
  const tx = -1 - offsetX * sx
  const ty = 1 - offsetY * sy

  // Column-major layout. Columns listed as:
  //   col0: (sx, 0, 0, 0)
  //   col1: (0, sy, 0, 0)
  //   col2: (0, 0, 1, 0)
  //   col3: (tx, ty, 0, 1)
  return new Float32Array([sx, 0, 0, 0, 0, sy, 0, 0, 0, 0, 1, 0, tx, ty, 0, 1])
}
